// frontend/src/pages/RestDayMonitoring/components/EmployeeRestDayTable.js
import React, { useState, useEffect, useMemo } from 'react';
import { 
  Activity,
  Building2,
  ChevronUp, 
  ChevronDown,
  Eye,
} from 'lucide-react';
import { StatusBadge } from '../../../components/UI/StatusBadge';
import { DepartmentTradeChips } from '../../../components/UI/DepartmentTradeChips';
import { pluralizeDays } from '../../../utils/numberUtils';
import { Pagination } from '../../../components/UI/Pagination';
import { FilterButton } from '../../../components/UI/FilterButton';
import PillSelect from '../../../components/UI/PillSelect';
import DebouncedSearchInput from '../../../components/UI/DebouncedSearchInput';

/**
 * StreakProgressBar - Visual progress indicator for consecutive working days (0-12)
 */
const StreakProgressBar = ({ days, max = 12 }) => {
  const percentage = Math.min(100, (days / max) * 100);
  // MOM ceiling is 12 consecutive days; a breach is 13+. 12 = at ceiling (critical),
  // 13+ = violated, 9-11 = warning, 7-8 = watch.
  const colorClass = days >= 13 ? 'bg-red-500' :
                     days >= 12 ? 'bg-red-400' :
                     days >= 9 ? 'bg-orange-500' :
                     days >= 7 ? 'bg-yellow-500' : 'bg-green-500';
  
  return (
    <div className="space-y-1 min-w-[120px]">
      <div className="flex justify-between text-xs">
        <span className="font-medium text-gray-700">{days} {pluralizeDays(days)}</span>
        <span className="text-gray-500">of {max}</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div 
          className={`h-2 rounded-full transition-all duration-300 ${colorClass}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};


/**
 * RestByIndicator - Shows the latest permissible rest day (MOM ceiling)
 * for employees already on a notable streak (watch tier and above), with
 * a today-relative qualifier so the table matches the digest language.
 */
const RestByIndicator = ({ requiredRestBy, days }) => {
  if (!requiredRestBy || days < 7) return null;

  const restDate = new Date(requiredRestBy + 'T00:00:00');
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.round((restDate - today) / (1000 * 60 * 60 * 24));

  let qualifier = '';
  let colorClass = 'text-gray-500';
  if (days >= 13 || diffDays < 0) {
    qualifier = ' · overdue';
    colorClass = 'text-red-600';
  } else if (diffDays === 0) {
    qualifier = ' · today';
    colorClass = 'text-red-500';
  } else if (diffDays === 1) {
    qualifier = ' · tomorrow';
    colorClass = 'text-orange-600';
  } else if (days >= 10) {
    colorClass = 'text-orange-600';
  }

  const formatted = restDate.toLocaleDateString('en-SG', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
  return (
    <p className={`text-xs font-medium ${colorClass} mt-1`}>
      Rest by {formatted}{qualifier}
    </p>
  );
};

/**
 * EmployeeRestDayTable - Sortable, filterable table of employees with rest day compliance
 * 
 * Features:
 * - Department and status filters
 * - Sortable columns (consecutive days desc by default = highest risk first)
 * - Streak progress bars showing days vs 12-day limit
 * - Department + trade identity chips (DS dense-table pattern)
 * - Design: admin red theme per design-system-reference.md
 */
const PAGE_SIZE = 25;

const EmployeeRestDayTable = ({ 
  employees, 
  loading, 
  filters, 
  onFilterChange,
  onViewEmployee,
}) => {
  const [sortField, setSortField] = useState('consecutiveWorkingDays');
  const [sortDirection, setSortDirection] = useState('desc');
  const [localSearch, setLocalSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Sort and filter
  const sortedEmployees = useMemo(() => {
    let filtered = [...employees];
    
    // Local search
    if (localSearch) {
      const search = localSearch.toLowerCase();
      filtered = filtered.filter(emp => 
        emp.fullName?.toLowerCase().includes(search) ||
        emp.staffId?.toLowerCase().includes(search) ||
        emp.department?.toLowerCase().includes(search)
      );
    }
    
    // Sort
    const statusOrder = { non_compliant: 5, critical: 4, warning: 3, watch: 2, compliant: 0 };
    filtered.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      if (sortField === 'complianceStatus') {
        aVal = statusOrder[aVal] ?? 0;
        bVal = statusOrder[bVal] ?? 0;
      } else if (typeof aVal === 'string') {
        aVal = (aVal || '').toLowerCase();
        bVal = (bVal || '').toLowerCase();
      }
      
      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });
    
    return filtered;
  }, [employees, localSearch, sortField, sortDirection]);

  // Unique departments
  const uniqueDepartments = useMemo(() => {
    const depts = new Set(employees.map(e => e.department).filter(Boolean));
    return Array.from(depts).sort();
  }, [employees]);

  // Reset to page 1 when filters, search, or sort change
  useEffect(() => {
    setCurrentPage(1);
  }, [localSearch, sortField, sortDirection, filters.status, filters.department]);

  // Client-side pagination
  const totalPages = Math.ceil(sortedEmployees.length / PAGE_SIZE);
  const paginatedEmployees = useMemo(() => 
    sortedEmployees.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [sortedEmployees, currentPage]
  );

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? 
      <ChevronUp className="w-4 h-4 ml-1" /> : 
      <ChevronDown className="w-4 h-4 ml-1" />;
  };

  // Loading skeleton
  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <div className="h-10 bg-gray-200 rounded animate-pulse w-full max-w-md" />
        </div>
        {/* Grid skeleton (small screens) */}
        <div className="md:hidden p-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="bg-gray-50 rounded-xl p-4 animate-pulse">
                <div className="flex items-center mb-3">
                  <div className="w-9 h-9 bg-gray-200 rounded-full mr-2.5" />
                  <div className="flex-1 space-y-1.5">
                    <div className="h-4 bg-gray-200 rounded w-2/3" />
                    <div className="h-3 bg-gray-200 rounded w-1/3" />
                  </div>
                </div>
                <div className="h-2 bg-gray-200 rounded-full w-full" />
              </div>
            ))}
          </div>
        </div>
        {/* Table skeleton (large screens) */}
        <div className="hidden md:block divide-y divide-gray-100">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="p-4 animate-pulse">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-gray-200 rounded-full mr-4" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-1/4" />
                  <div className="h-3 bg-gray-200 rounded w-1/6" />
                </div>
                <div className="h-6 bg-gray-200 rounded w-20 mx-4" />
                <div className="h-4 bg-gray-200 rounded w-24" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
      {/* Toolbar */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center gap-3">
          {/* Search */}
          <DebouncedSearchInput
            value={localSearch}
            onSearch={setLocalSearch}
            placeholder="Search employees..."
            className="max-w-xl"
          />

          {/* Filter button */}
          <div className="ml-auto">
            <FilterButton
            theme="admin"
            isOpen={showFilters}
            activeCount={[filters.department, filters.status].filter(Boolean).length}
            onClick={() => setShowFilters(v => !v)}
          />
          </div>
        </div>

        {/* Collapsible filter panel */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Department Filter */}
              <div>
                <label className="form-label-admin">Department</label>
                <PillSelect
                  options={[
                    { value: '', label: 'All Departments' },
                    ...uniqueDepartments.map(dept => ({ value: dept, label: dept }))
                  ]}
                  value={filters.department || ''}
                  onChange={(val) => onFilterChange({ department: val })}
                  theme="admin"
                  icon={Building2}
                  iconColor="text-red-600"
                  className="w-full"
                />
              </div>

              {/* Status Filter */}
              <div>
                <label className="form-label-admin">Status</label>
                <PillSelect
                  options={[
                    { value: '', label: 'All Statuses' },
                    { value: 'action_required', label: 'Action Required' },
                    { value: 'watch_list', label: 'Watch List' },
                    { isGroupHeader: true, label: '──────────' },
                    { value: 'non_compliant', label: 'Violated (13+ days)' },
                    { value: 'critical', label: 'Critical (12 days — at limit)' },
                    { value: 'warning', label: 'Warning (9-11 days)' },
                    { value: 'watch', label: 'Watch (7-8 days)' },
                    { value: 'compliant', label: 'OK (<7 days)' },
                  ]}
                  value={filters.status || ''}
                  onChange={(val) => onFilterChange({ status: val })}
                  theme="admin"
                  icon={Activity}
                  iconColor="text-red-600"
                  className="w-full"
                />
              </div>
            </div>
          </div>
        )}

        {/* Results count */}
        <div className="mt-3 text-sm text-gray-600">
          <span className="font-medium text-gray-900">{sortedEmployees.length}</span> employees{localSearch && <span> matching "{localSearch}"</span>}
        </div>
      </div>

      {/* Grid View (small screens) */}
      <div className="md:hidden">
        {sortedEmployees.length === 0 ? (
          <div className="px-6 py-12 text-center text-gray-500">
            No employees found matching your criteria
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3">
            {paginatedEmployees.map(employee => (
              <button
                key={employee.staffId}
                onClick={() => onViewEmployee?.(employee)}
                className="bg-gray-50 rounded-xl p-4 text-left hover:bg-gray-100 transition-colors border border-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center min-w-0">
                    <div className="w-9 h-9 bg-red-100 rounded-full flex items-center justify-center mr-2.5 flex-shrink-0">
                      <span className="text-xs font-medium text-red-600">
                        {employee.firstName?.charAt(0)}{employee.lastName?.charAt(0)}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{employee.fullName}</p>
                      <div className="mt-0.5">
                        <DepartmentTradeChips
                          department={employee.department}
                          trade={employee.trade}
                        />
                      </div>
                    </div>
                  </div>
                  <StatusBadge status={employee.complianceStatus} />
                </div>
                <StreakProgressBar days={employee.consecutiveWorkingDays} />
                <RestByIndicator requiredRestBy={employee.requiredRestBy} days={employee.consecutiveWorkingDays} />
                <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
                  <span>
                    Last rest: {employee.lastRestDayDate 
                      ? new Date(employee.lastRestDayDate + 'T00:00:00').toLocaleDateString('en-SG', { day: 'numeric', month: 'short' })
                      : 'N/A'
                    }
                  </span>
                  <Eye className="w-3.5 h-3.5 text-gray-400" />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Table View (large screens) */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50">
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-600 cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('fullName')}
              >
                <div className="flex items-center">
                  Employee
                  <SortIcon field="fullName" />
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-600 cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('department')}
              >
                <div className="flex items-center">
                  Department
                  <SortIcon field="department" />
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-600 cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('consecutiveWorkingDays')}
              >
                <div className="flex items-center">
                  Consecutive Days
                  <SortIcon field="consecutiveWorkingDays" />
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-600 cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('complianceStatus')}
              >
                <div className="flex items-center">
                  Status
                  <SortIcon field="complianceStatus" />
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-600">
                Last Rest Day
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-600">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {sortedEmployees.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                  No employees found matching your criteria
                </td>
              </tr>
            ) : (
              paginatedEmployees.map(employee => (
                <tr 
                  key={employee.staffId} 
                  className="table-row"
                >
                  {/* Employee */}
                  <td className="px-6 py-3">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                        <span className="text-xs font-medium text-red-600">
                          {employee.firstName?.charAt(0)}{employee.lastName?.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{employee.fullName}</p>
                        <p className="text-xs text-gray-500">{employee.staffId}</p>
                      </div>
                    </div>
                  </td>
                  
                  {/* Department + Trade — dense chips (dept primary, trade secondary) */}
                  <td className="px-6 py-3">
                    <DepartmentTradeChips
                      department={employee.department}
                      trade={employee.trade}
                    />
                  </td>
                  
                  {/* Consecutive Days with Progress Bar */}
                  <td className="px-6 py-3">
                    <StreakProgressBar days={employee.consecutiveWorkingDays} />
                    <RestByIndicator requiredRestBy={employee.requiredRestBy} days={employee.consecutiveWorkingDays} />
                  </td>
                  
                  {/* Status */}
                  <td className="px-6 py-3 whitespace-nowrap">
                    <StatusBadge status={employee.complianceStatus} />
                  </td>

                  {/* Last Rest Day */}
                  <td className="px-6 py-3">
                    <p className="text-sm text-gray-900">
                      {employee.lastRestDayDate 
                        ? new Date(employee.lastRestDayDate + 'T00:00:00').toLocaleDateString('en-SG', { day: 'numeric', month: 'short', year: 'numeric' })
                        : 'N/A'
                      }
                    </p>
                  </td>
                  
                  {/* Actions */}
                  <td className="px-6 py-3 text-right">
                    <button
                      onClick={() => onViewEmployee?.(employee)}
                      className="icon-button-admin"
                      title="View Details"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        totalCount={sortedEmployees.length}
        pageSize={PAGE_SIZE}
        theme="admin"
        noun="employees"
      />

      {/* Footer */}
      {sortedEmployees.length > 0 && (
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>
              {sortedEmployees.filter(e => e.complianceStatus !== 'compliant').length} employees require attention
            </span>
            <span>
              Longest streak: {Math.max(...sortedEmployees.map(e => e.consecutiveWorkingDays), 0)} days
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeRestDayTable;
